import { Flex, MultiSelectProps, Text } from "@mantine/core";
import { forwardRef, useMemo, useState } from "react";

import { FKInfoLabel, FKMultiSelect } from "@/features/ui/Base/List/CustomFilter/MultiField";
import { IconX } from "@tabler/icons-react";
import {
  useAbstractItemsMultiAutoComplete,
  useAbstractItemsMultiCode,
} from "./multiAuto-useGetAbstractItemsQuery";

interface AbstractItemsMultiProps extends React.ComponentPropsWithoutRef<"div"> {
  label: string;
  value: string;
}

export interface AbstractItemsMultiAutoCompleteProps
  extends Partial<MultiSelectProps>,
    Partial<React.RefAttributes<HTMLInputElement>> {
  value: string[];
  onChange?: (e: string[]) => void; // (value: string[]) => React.Dispatch<React.SetStateAction<string>>;
  maxDropdownHeight?: number;
  width?: string;
}

export const AbstractItemsMultiAutoComplete = (params: AbstractItemsMultiAutoCompleteProps) => {
  const { value: abstractItems, onChange, maxDropdownHeight, width, ...etcParams } = params;
  const [focused, setFocused] = useState<boolean>(true);
  const [searchKeyword, setSearchKeyword] = useState<string>("");
  const { data: options } = useAbstractItemsMultiAutoComplete(focused, searchKeyword);
  const { data: initialOptions } = useAbstractItemsMultiCode(
    !!searchKeyword,
    searchKeyword ?? null,
  );

  const onChangeHandler = (e: string[]) => {
    onChange && onChange(e);
  };

  const SelectAbstractItem = forwardRef<HTMLDivElement, AbstractItemsMultiProps>(
    ({ label: name, value: code, ...others }, ref) => (
      <div ref={ref} {...others}>
        <Flex direction="row" justify="space-between" align="center">
          <Flex align={"center"}>
            <Text>{name}</Text>
            <Text fz="xs">(Color명: {code})</Text>
          </Flex>
        </Flex>
      </div>
    ),
  );

  // data prop에 들어가는 중복 제거된 data를 useMemo로 감싸기
  const filteredOptions = useMemo(() => {
    const combinedOptions = [
      ...(options ?? []),
      ...(initialOptions ?? []),
      ...(abstractItems.map((value) => ({ label: value, value })) ?? []),
    ];

    return combinedOptions.reduce(
      (unique: AbstractItemsMultiProps[], option: AbstractItemsMultiProps) =>
        unique.some((u) => u.value === option.value) ? unique : [...unique, option],
      [],
    );
  }, [options, initialOptions, abstractItems]);

  return (
    <FKMultiSelect
      clearable
      value={abstractItems}
      data={filteredOptions}
      onChange={onChangeHandler}
      searchable
      itemComponent={SelectAbstractItem}
      searchValue={searchKeyword}
      withinPortal
      onDropdownOpen={() => setFocused(true)}
      onDropdownClose={() => setFocused(false)}
      maxDropdownHeight={maxDropdownHeight ?? 150}
      onSearchChange={setSearchKeyword}
      nothingFound="No Options"
      rightSection={AbstractItemInfo({
        AbstractItem: abstractItems,
        onChange: onChangeHandler,
      })}
      {...etcParams}
    />
  );
};

const AbstractItemInfo = (params: { AbstractItem?: string[]; onChange: (e: string[]) => void }) => {
  const { AbstractItem, onChange } = params;

  const clearHandler: React.MouseEventHandler<SVGSVGElement> = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onChange([]);
  };

  return AbstractItem?.length ? (
    <FKInfoLabel>
      <IconX size="1rem" onClick={clearHandler} />
    </FKInfoLabel>
  ) : null;
};
